import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { useMemo, useState } from 'react'
import { type ProgramBreakdownRow } from '../../../../models/peerToPeer/Dashboard'
import { tableStyles } from '../../../customComponents/dataTable/tableStyles'
import { SortCaretsIcon } from '../../../customComponents/dataTable/SortCaretsIcon'
import { type ColSpec } from './goalViews'
import { SectionCard } from './SectionCard'

const AUTO_COLUMN_MIN_WIDTH = 140

const compare = (a: ProgramBreakdownRow[keyof ProgramBreakdownRow], b: ProgramBreakdownRow[keyof ProgramBreakdownRow]): number =>
  typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))

interface Props {
  rows: ProgramBreakdownRow[]
  columns: ColSpec[]
}

export const ProgramBreakdownTable = ({ rows, columns }: Props): JSX.Element => {
  const [sortKey, setSortKey] = useState<keyof ProgramBreakdownRow>('building')
  const [ascending, setAscending] = useState(true)

  const sorted = useMemo(
    () => [...rows].sort((a, b) => ascending ? compare(a[sortKey], b[sortKey]) : -compare(a[sortKey], b[sortKey])),
    [rows, sortKey, ascending]
  )

  const sortBy = (key: keyof ProgramBreakdownRow): void => {
    if (sortKey === key) { setAscending(!ascending) } else { setSortKey(key); setAscending(true) }
  }

  const minTableWidth = columns.reduce((total, column) => total + (column.width ?? AUTO_COLUMN_MIN_WIDTH), 0)

  return (
    <SectionCard title='Program Breakdown Details'>
      <Box sx={tableStyles.container}>
        <Table data-testid='p2pProgramBreakdown' sx={{ tableLayout: 'fixed', minWidth: `${minTableWidth}px` }}>
          <TableHead>
            <TableRow>
              {columns.map(column => {
                const active = sortKey === column.key
                return (
                  <TableCell
                    key={column.label}
                    onClick={() => { sortBy(column.key) }}
                    aria-sort={active ? (ascending ? 'ascending' : 'descending') : undefined}
                    sx={{ ...tableStyles.headCell, ...tableStyles.sortableHeadCell, width: column.width }}
                  >
                    <Box component='span' sx={tableStyles.headLabel}>
                      {column.label}
                      <SortCaretsIcon direction={active ? (ascending ? 'asc' : 'desc') : null} />
                    </Box>
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, index) => (
              <TableRow key={index} sx={tableStyles.row}>
                {columns.map(column => {
                  const text = String(row[column.key])
                  return (
                    <TableCell key={column.label} sx={tableStyles.bodyCell} title={column.render == null ? text : undefined}>
                      {column.render != null ? column.render(row) : text}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </SectionCard>
  )
}
